import React, {
  useState,
  createContext,
  useRef,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./Estimate.css";
import WorkTab from "./WorkTab/WorkTab";
import ProjectTab from "./ProjectTab/ProjectTab";
import LeadTab from "./LeadTab/LeadTab";
import QualityTab from "./QualityTab/QualityTab";
import BimTab from "./BimTab/BimTab";
import ContractSectionsTab from "./ContractSectionsTab/ContractSectionsTab";
import VersionHistoryDrawer from "./VersionHistoryDrawer/VersionHistoryDrawer";
import CorrectionHistoryDrawer from "./CorrectionHistoryDrawer/CorrectionHistoryDrawer";
import ApprovalModal from "./ApprovalModal/ApprovalModal";
import { EstimateStateProvider, useEstimateState } from "./EstimateContext";
import {
  createCorrectionLayer,
  buildCorrectionLayersFromRecords,
  buildCorrectionLayersFromDetailed,
  getEstimateSectionIds,
  correctionCellKey,
  correctionLayerComment,
  correctionImpactFromRecord,
  correctionImpactMoney,
  parseCorrectionTeiId,
  UNIFIED_CORRECTION_KEY,
  collectWorkCorrectionRecordIds,
  collectModuleCorrectionRecordIds,
  isEmptyCorrectionLayer,
  sumAllCorrectionImpact,
  preserveDraftCorrectionLayer,
  parseIncomeCorrectionPayload,
  incomeCorrectionListTotals,
  formatIncomeCorrectionAmount,
  pairIncomeCorrectionsToLayers,
  findIncomeCorrectionForLayer,
  parseCorrectionLayerNumber,
  ensureLayersForIncomeCorrections,
  formatSignedDelta,
  moduleCorrectionCellKey,
  ESTIMATE_CORRECTION_MODULES,
} from "./correctionStorage";
import Preloader from "../../../Preloader/Preloader";
import {
  requestProjectEstimateApproval,
  restoreVersion,
  getProjectEstimateApprovals,
  updateThresholdCostPercent,
  getSectionLaborEffortCorrections,
  saveSectionLaborEffortCorrection,
  patchLaborEffortCorrection,
  deleteLaborEffortCorrection,
  getModuleLaborEffortCorrections,
  saveModuleLaborEffortCorrection,
  patchModuleLaborEffortCorrection,
  deleteModuleLaborEffortCorrection,
  getProjectIncomeCorrection,
  saveProjectIncomeCorrection,
  deleteProjectIncomeCorrection,
} from "../../../../api/projectEstimatesApi";
import { getProjectById } from "../../../../api/projectApi";
import {
  getProjectApprovalStatuses,
  cancelApprovalRequest,
  getProjectCorrectionApprovals,
  requestCorrectionsApproval,
  cancelCorrectionsApprovalRequest,
} from "../../../../api/projectApprovalApi";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  findActiveApproval,
  formatCorrectionApprovalAmount,
  getApprovalStatusKind,
} from "../../../Approvals/ProjectApproval/projectApprovalHelpers";

// Импорты иконок
import Coinssvg from "../../../../images/projects/ProjectFinance/Coinssvg.svg";
import Calc from "../../../../images/projects/ProjectFinance/Calc.svg";
import House from "../../../../images/projects/ProjectFinance/House.svg";
import Sigma from "../../../../images/projects/ProjectFinance/Sigma.svg";

registerLocale("ru", ru);

// Контекст для передачи данных между компонентами
export const EstimateContext = createContext();

const bottomTabs = [
  { id: "work", label: "Рабочее проектирование" },
  { id: "bim", label: "BIM координирование" },
  { id: "quality", label: "Контроль качества" },
  { id: "lead", label: "Ведение проекта" },
  { id: "project", label: "Управление проектом" },
  { id: "contract", label: "Разделы на договоре" },
];

const CLIENT_AMOUNT_INPUT_RE = /^-?\d[\d\s]*([.,]\d*)?$/;

const parseClientAmountInput = (raw) => {
  const cleaned = String(raw ?? "")
    .replace(/\u00a0/g, "")
    .replace(/\s/g, "")
    .replace(",", ".");
  if (
    cleaned === "" ||
    cleaned === "-" ||
    cleaned === "." ||
    cleaned === "-."
  ) {
    return 0;
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const formatClientAmountDraft = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return n.toLocaleString("ru-RU");
};

const clampNdsPercent = (value, fallback = 0) => {
  const n = Number(value);
  const source = Number.isFinite(n) ? n : Number(fallback) || 0;
  if (!Number.isFinite(source)) return 0;
  return Math.min(100, Math.max(0, source));
};

const isEstimateLockedByApprovals = (currentVersion, ...approvalLists) => {
  const approvals = approvalLists.flat().filter(Boolean);
  if (approvals.length === 0) return false;

  const versionNumbers = approvals
    .map((approval) => approval.version)
    .filter((version) => version !== null && version !== undefined)
    .map(Number);
  const maxApprovalVersion =
    versionNumbers.length > 0 ? Math.max(...versionNumbers) : null;
  if (
    currentVersion != null &&
    maxApprovalVersion != null &&
    Number(currentVersion) > maxApprovalVersion
  ) {
    return false;
  }

  return approvals.some((approval) => {
    if (!approval?.status) return false;
    const kind = getApprovalStatusKind(approval.status);
    return kind === "approved" || kind === "pending";
  });
};

const isEstimateVersionApproved = (currentVersion, ...approvalLists) => {
  const approvals = approvalLists.flat().filter(Boolean);
  const versionNumbers = approvals
    .map((approval) => approval.version)
    .filter((version) => version !== null && version !== undefined)
    .map(Number);
  const maxApprovalVersion =
    versionNumbers.length > 0 ? Math.max(...versionNumbers) : null;
  if (
    currentVersion != null &&
    maxApprovalVersion != null &&
    Number(currentVersion) > maxApprovalVersion
  ) {
    return false;
  }

  return approvals.some((approval) => {
    if (!approval?.status) return false;
    if (getApprovalStatusKind(approval.status) !== "approved") return false;
    if (approval.version == null || currentVersion == null) return true;
    return Number(approval.version) === Number(currentVersion);
  });
};

const approvalBelongsToVersion = (approval, currentVersion) => {
  if (currentVersion == null || approval?.version == null) return true;
  return Number(approval.version) === Number(currentVersion);
};

const approvalsForEstimateVersion = (approvals, currentVersion) =>
  (Array.isArray(approvals) ? approvals : []).filter((approval) =>
    approvalBelongsToVersion(approval, currentVersion),
  );

const formatCompareMoney = (value) =>
  `${Math.round(Number(value) || 0).toLocaleString("ru-RU")} ₸`;

// удалён: TableCell (неиспользуется)

// удалён: ProfitCircle (неиспользуется)

/**
 * Компонент сметы
 *
 * ОБНОВЛЕНО:
 * - Использует новые функции API через контекст (loadEstimateData)
 * - Упрощена логика загрузки данных
 * - Удалены дублирующие вызовы API
 * - Восстановлен блок прибыли справа с данными из API
 *
 * СООТВЕТСТВИЕ ПОЛЕЙ API:
 * - contract_value: Сумма основного договора
 * - contract_value_before_nds: Сумма за вычетом НДС (также используется как Доход)
 * - contract_value_construction: На разработку
 * - contract_value_construction_percent: процент На разработку
 * - cost_price: Себестоимость-план
 * - difference: Отклонения
 * - nds_value: сумма НДС
 * - indirect_expenditures: Косвенные расходы
 * - indirect_expenditures_percent: Процент косвенных расходов
 * - cost_price_percent: процент Себестоимость-план
 * - net_profit: Чистая прибыль
 * - net_profit_percent: процент Чистая прибыль
 */
const EstimateComponent = ({
  projectId,
  onSave,
  isReadOnly = false,
  projectLockedByApproval = false,
}) => {
  // Получаем функцию сохранения из контекста
  const { performSave } = useEstimateState();
  const { userData } = useContext(UserContext);

  const [activeBottomTab, setActiveBottomTab] = useState(() => {
    // Проверяем, есть ли сохраненное состояние для этого проекта
    const savedBottomTab = localStorage.getItem(
      `estimate_activeBottomTab_${projectId}`,
    );

    // Если есть сохраненное состояние - используем его
    // Если нет - показываем первую вкладку (первый вход на проект)
    return savedBottomTab || "work";
  });
  // удалено: expandedRows
  const [showVersionDrawer, setShowVersionDrawer] = useState(false);
  const [showCorrectionDrawer, setShowCorrectionDrawer] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [incomeCorrections, setIncomeCorrections] = useState([]);
  const [incomeCorrectionTotal, setIncomeCorrectionTotal] = useState(null);
  const [clientAmountDraft, setClientAmountDraft] = useState("");
  const [clientAmountWithNds, setClientAmountWithNds] = useState(false);
  const [clientAmountNdsPercent, setClientAmountNdsPercent] = useState(0);
  const [incomeCorrectionSaving, setIncomeCorrectionSaving] = useState(false);
  const correctionsRef = useRef(corrections);
  const incomeCorrectionsRef = useRef(incomeCorrections);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showCorrectionApprovalModal, setShowCorrectionApprovalModal] =
    useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const workTabRef = useRef();
  const bimTabRef = useRef();
  const qualityTabRef = useRef();
  const projectTabRef = useRef();
  const leadTabRef = useRef();
  const contractSectionsTabRef = useRef();

  // Состояния для данных шапки
  const [headerData, setHeaderData] = useState({
    contract_value: 0,
    contract_value_before_nds: 0,
    contract_value_construction: 0,
    contract_value_construction_percent: 0,
    cost_price: 0,
    difference: 0,
    // Добавляем все остальные поля из API
    nds_value: 0,
    nds_percent: 0,
    total_sum: 0,
    total_sum_percent: 0,
    indirect_expenditures: 0,
    indirect_expenditures_percent: 0,
    cost_price_percent: 0,
    pure_income: 0,
    pure_income_percent: 0,
    net_profit: 0,
    net_profit_percent: 0,
    total_construction_design: {},
    bonus_percent_construction_design: 0,
    bonus_percent_aup_aho: 0,
    bonus_aup_aho: 0,
    total_direct_construction_design: 0,
    bim_coordination: {},
    quality_control: {},
    project_delivery: {},
    project_management: {},
    total_with_nds_from_cp: 0,
  });

  // Состояния для редактируемых инпутов
  // удалены: демо-поля avgRate/bonusPercent/laborCosts

  // Состояния для полей заявки
  const [contractNumber, setContractNumber] = useState("001");
  const [contractDate, setContractDate] = useState(new Date("2025-01-01"));

  // Состояния для согласования сметы
  const [projectData, setProjectData] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [isCancellingApproval, setIsCancellingApproval] = useState(false);
  const [isCreatingCopy, setIsCreatingCopy] = useState(false);
  const [estimateApprovals, setEstimateApprovals] = useState([]); // Список согласований сметы с версиями
  const [correctionApprovals, setCorrectionApprovals] = useState([]);
  const [isSubmittingCorrectionApproval, setIsSubmittingCorrectionApproval] =
    useState(false);
  const [isCancellingCorrectionApproval, setIsCancellingCorrectionApproval] =
    useState(false);

  // Для второй вкладки:
  // удалены: managePercent/manageBonusPercent/manageLabor

  // Получаем функции из нового контекста состояния
  const {
    updateEstimateSection,
    loadEstimateData,
    estimateData,
    sectionDetails,
    isLoading: contextLoading,
    error: contextError,
  } = useEstimateState();

  const sectionDetailsRef = useRef(sectionDetails);
  useEffect(() => {
    correctionsRef.current = corrections;
  }, [corrections]);
  useEffect(() => {
    incomeCorrectionsRef.current = incomeCorrections;
  }, [incomeCorrections]);
  useEffect(() => {
    sectionDetailsRef.current = sectionDetails;
  }, [sectionDetails]);

  const currentCorrection = corrections[corrections.length - 1] || null;
  const incomeByLayer = useMemo(
    () => pairIncomeCorrectionsToLayers(incomeCorrections, corrections),
    [incomeCorrections, corrections],
  );
  const currentLayerIncome = currentCorrection
    ? incomeByLayer.get(currentCorrection.number) || null
    : null;
  const currentLayerIncomeId = currentLayerIncome?.id ?? null;
  const currentLayerIncomeAmount = currentLayerIncome?.amount ?? null;
  const currentLayerIncomeNds = currentLayerIncome?.nds_included_in_amount;
  const currentLayerIncomeNdsPercent =
    currentLayerIncome?.nds_percent != null
      ? Number(currentLayerIncome.nds_percent)
      : null;

  useEffect(() => {
    setClientAmountDraft(formatClientAmountDraft(currentLayerIncomeAmount));
    setClientAmountWithNds(Boolean(currentLayerIncomeNds));
    setClientAmountNdsPercent(
      clampNdsPercent(currentLayerIncomeNdsPercent, headerData.nds_percent),
    );
  }, [
    currentCorrection?.id,
    currentCorrection?.number,
    currentLayerIncomeId,
    currentLayerIncomeAmount,
    currentLayerIncomeNds,
    currentLayerIncomeNdsPercent,
    headerData.nds_percent,
  ]);
  const incomeCorrectionSyncKey = [
    estimateData?.version ?? "",
    ...(estimateData?.income_correction?.items || []).map(
      (item) =>
        `${item?.id}:${item?.amount}:${item?.nds_included_in_amount}:${item?.nds_percent}`,
    ),
    estimateData?.income_correction?.total?.amount_before_nds ?? "",
    estimateData?.income_correction?.id ?? "",
    estimateData?.income_correction?.amount ?? "",
  ].join("|");

  const sectionIdsKey = useMemo(
    () => getEstimateSectionIds(estimateData).join(","),
    [estimateData],
  );

  const correctionSectionIds = useMemo(
    () =>
      sectionIdsKey
        ? sectionIdsKey
            .split(",")
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : [],
    [sectionIdsKey],
  );

  const correctionSectionsLookup = useMemo(() => {
    const lookup = {};
    const allSections = [
      ...(estimateData?.construction_design || []),
      ...(estimateData?.construction_design_project || []),
    ];
    allSections.forEach((item) => {
      const id = item?.section?.id;
      if (id == null) return;
      lookup[id] = {
        name: item.section?.name || `Раздел ${id}`,
        blocks: {},
      };
    });
    Object.values(sectionDetails || {}).forEach((detail) => {
      const sectionId = detail?.section_id;
      if (sectionId == null) return;
      if (!lookup[sectionId]) {
        lookup[sectionId] = {
          name: `Раздел ${sectionId}`,
          blocks: {},
        };
      }
      (detail.block_expenditures || []).forEach((block) => {
        const teiId = block?.block?.tei_id;
        if (teiId == null) return;
        lookup[sectionId].blocks[teiId] = block.block?.name || `ТЭИ ${teiId}`;
      });
    });
    return lookup;
  }, [estimateData, sectionDetails]);

  const [developmentPercentApplied, setDevelopmentPercentApplied] =
    useState("0");
  const [developmentPercentDraft, setDevelopmentPercentDraft] = useState("0");
  const [isDevelopmentPercentEditing, setIsDevelopmentPercentEditing] =
    useState(false);
  const [isDevelopmentPercentSubmitting, setIsDevelopmentPercentSubmitting] =
    useState(false);

  const parsePercentValue = (rawValue) => {
    const parsed = Number.parseFloat(String(rawValue).replace(",", "."));
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(parsed, 0);
  };

  const formatPercentDisplay = (value) => {
    if (!Number.isFinite(value)) return "0";
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2).replace(/\.?0+$/, "");
  };

  // Функция для обновления данных шапки
  const updateHeaderData = (data) => {
    setHeaderData((prev) => {
      const newData = {
        ...prev,
        // Не обновляем статичные поля договора
        contract_value: prev.contract_value,
        contract_value_before_nds: prev.contract_value_before_nds,
        contract_value_construction:
          data.contract_value_construction !== undefined
            ? data.contract_value_construction
            : prev.contract_value_construction,
        // Обновляем только динамические поля
        contract_value_construction_percent:
          data.contract_value_construction_percent !== undefined
            ? data.contract_value_construction_percent
            : prev.contract_value_construction_percent,
        cost_price:
          data.cost_price !== undefined ? data.cost_price : prev.cost_price,
        difference:
          data.difference !== undefined ? data.difference : prev.difference,
        // Обновляем все остальные поля из API
        nds_value:
          data.nds_value !== undefined ? data.nds_value : prev.nds_value,
        nds_percent:
          data.nds_percent !== undefined ? data.nds_percent : prev.nds_percent,
        total_sum:
          data.total_sum !== undefined ? data.total_sum : prev.total_sum,
        total_sum_percent:
          data.total_sum_percent !== undefined
            ? data.total_sum_percent
            : prev.total_sum_percent,
        indirect_expenditures:
          data.indirect_expenditures !== undefined
            ? data.indirect_expenditures
            : prev.indirect_expenditures,
        indirect_expenditures_percent:
          data.indirect_expenditures_percent !== undefined
            ? data.indirect_expenditures_percent
            : prev.indirect_expenditures_percent,
        cost_price_percent:
          data.cost_price_percent !== undefined
            ? data.cost_price_percent
            : prev.cost_price_percent,
        pure_income:
          data.pure_income !== undefined ? data.pure_income : prev.pure_income,
        pure_income_percent:
          data.pure_income_percent !== undefined
            ? data.pure_income_percent
            : prev.pure_income_percent,
        net_profit:
          data.net_profit !== undefined ? data.net_profit : prev.net_profit,
        net_profit_percent:
          data.net_profit_percent !== undefined
            ? data.net_profit_percent
            : prev.net_profit_percent,
        total_construction_design:
          data.total_construction_design !== undefined
            ? data.total_construction_design
            : prev.total_construction_design,
        bonus_percent_construction_design:
          data.bonus_percent_construction_design !== undefined
            ? data.bonus_percent_construction_design
            : prev.bonus_percent_construction_design,
        bonus_percent_aup_aho:
          data.bonus_percent_aup_aho !== undefined
            ? data.bonus_percent_aup_aho
            : prev.bonus_percent_aup_aho,
        bonus_aup_aho:
          data.bonus_aup_aho !== undefined
            ? data.bonus_aup_aho
            : prev.bonus_aup_aho,
        total_direct_construction_design:
          data.total_direct_construction_design !== undefined
            ? data.total_direct_construction_design
            : prev.total_direct_construction_design,
        total_with_nds_from_cp:
          data.total_with_nds_from_cp !== undefined
            ? data.total_with_nds_from_cp
            : prev.total_with_nds_from_cp,
        // Фактическая сумма долей из таблицы (Рабочее проектирование) — для проверки «> 100%» и кнопки Сохранить
        percentTotalFromTable:
          data.percentTotalFromTable !== undefined
            ? data.percentTotalFromTable
            : prev.percentTotalFromTable,
      };

      return newData;
    });

    // Обновляем данные вкладок через новый контекст состояния.
    // Не трогаем пользовательские роли и трудозатраты — /calculate часто возвращает дефолт/0.
    if (data.bim_coordination) {
      const { roles, bim_role_salaries, ...bimRest } = data.bim_coordination;
      updateEstimateSection("bim_coordination", bimRest);
    }
    if (data.quality_control) {
      const {
        roles,
        quality_control_role_salaries,
        normative_control_efforts,
        ...qualityRest
      } = data.quality_control;
      updateEstimateSection("quality_control", qualityRest);
    }
    if (data.project_delivery) {
      const { roles, project_delivery_role_salaries, ...deliveryRest } =
        data.project_delivery;
      updateEstimateSection("project_delivery", deliveryRest);
    }
    if (data.project_management) {
      const {
        roles,
        management_role_salaries,
        administration_efforts,
        ...pmRest
      } = data.project_management;
      updateEstimateSection("project_management", pmRest);
    }
  };

  // Загрузка данных при изменении projectId
  useEffect(() => {
    if (!projectId) return;

    // Используем новую функцию загрузки из контекста
    loadEstimateData(projectId);
  }, [projectId, loadEstimateData]);

  // Загрузка данных проекта для согласования
  useEffect(() => {
    const loadProjectData = async () => {
      if (!projectId) return;

      try {
        const data = await getProjectById(projectId);
        setProjectData(data);
      } catch (error) {
        console.error("Ошибка при загрузке данных проекта:", error);
      }
    };

    loadProjectData();
  }, [projectId]);

  // Загрузка статусов согласования
  useEffect(() => {
    const loadApprovalStatuses = async () => {
      if (!projectId) return;

      try {
        const statuses = await getProjectApprovalStatuses(parseInt(projectId));
        setApprovalStatuses(statuses);
      } catch (error) {
        console.error("Ошибка при загрузке статусов согласования:", error);
        // Не критично, продолжаем работу
      }
    };

    loadApprovalStatuses();
  }, [projectId]);

  // Загрузка списка согласований сметы для сравнения версий
  useEffect(() => {
    const loadEstimateApprovals = async () => {
      if (!projectId) return;

      try {
        const approvals = await getProjectEstimateApprovals(projectId);
        setEstimateApprovals(approvals || []);
      } catch (error) {
        console.error("Ошибка при загрузке списка согласований сметы:", error);
        // Не критично, продолжаем работу
        setEstimateApprovals([]);
      }
    };

    loadEstimateApprovals();
  }, [projectId]);

  // Загрузка согласований корректировок
  useEffect(() => {
    const loadCorrectionApprovals = async () => {
      if (!projectId) return;

      try {
        const approvals = await getProjectCorrectionApprovals(
          parseInt(projectId),
        );
        setCorrectionApprovals(approvals || []);
      } catch (error) {
        console.error("Ошибка при загрузке согласований корректировок:", error);
        setCorrectionApprovals([]);
      }
    };

    loadCorrectionApprovals();
  }, [projectId]);

  // Обновление данных шапки при изменении данных в контексте
  useEffect(() => {
    if (!estimateData) return;

    // Обновляем шапку при каждом обновлении estimateData,
    // чтобы после точечных API (например threshold-cost-percent) UI сразу показывал новые суммы.
    setHeaderData((prev) => ({
      ...prev,
      contract_value: estimateData.contract_value,
      contract_value_before_nds: estimateData.contract_value_before_nds,
      contract_value_construction: estimateData.contract_value_construction,
      contract_value_construction_percent:
        estimateData.contract_value_construction_percent,
      cost_price: estimateData.cost_price,
      difference: estimateData.difference,
      nds_value: estimateData.nds_value,
      nds_percent: estimateData.nds_percent,
      total_sum: estimateData.total_sum,
      total_sum_percent: estimateData.total_sum_percent,
      indirect_expenditures: estimateData.indirect_expenditures,
      indirect_expenditures_percent: estimateData.indirect_expenditures_percent,
      cost_price_percent: estimateData.cost_price_percent,
      pure_income: estimateData.pure_income,
      pure_income_percent: estimateData.pure_income_percent,
      net_profit: estimateData.net_profit,
      net_profit_percent: estimateData.net_profit_percent,
      total_construction_design: estimateData.total_construction_design || {},
      bonus_percent_construction_design:
        estimateData.bonus_percent_construction_design,
      bonus_percent_aup_aho: estimateData.bonus_percent_aup_aho,
      bonus_aup_aho: estimateData.bonus_aup_aho,
      total_direct_construction_design:
        estimateData.total_direct_construction_design,
      total_with_nds_from_cp: estimateData.total_with_nds_from_cp || 0,
    }));
  }, [estimateData]);

  useEffect(() => {
    const percentValue = Number(headerData.contract_value_construction_percent);
    if (!Number.isFinite(percentValue)) {
      setDevelopmentPercentApplied("0");
      setDevelopmentPercentDraft("0");
      return;
    }
    const formatted = formatPercentDisplay(percentValue);
    setDevelopmentPercentApplied(formatted);
    setDevelopmentPercentDraft(formatted);
  }, [headerData.contract_value_construction_percent]);

  // Обновление состояния загрузки и ошибок из контекста
  useEffect(() => {
    setLoading(contextLoading);
    setError(contextError);
  }, [contextLoading, contextError]);

  // Сохраняем состояние нижней вкладки при изменении projectId
  useEffect(() => {
    if (projectId) {
      // Сохраняем состояние только если пользователь активно переключил вкладку
      // (не при первом входе на проект)
      if (
        activeBottomTab !== "work" ||
        localStorage.getItem(`estimate_activeBottomTab_${projectId}`)
      ) {
        localStorage.setItem(
          `estimate_activeBottomTab_${projectId}`,
          activeBottomTab,
        );
      }
    }
  }, [activeBottomTab, projectId]);

  useEffect(() => {
    setCorrections([]);
    setIncomeCorrections([]);
    setIncomeCorrectionTotal(null);
    setClientAmountDraft("");
    setClientAmountWithNds(false);
    setClientAmountNdsPercent(0);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;

    const loadCorrectionLayers = async () => {
      if (!projectId || contextLoading) {
        return;
      }

      const sectionIds = sectionIdsKey
        ? sectionIdsKey
            .split(",")
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : [];
      const currentDetails = sectionDetailsRef.current;

      setCorrectionsLoading(true);
      try {
        const [sectionLists, moduleLists] = await Promise.all([
          Promise.all(
            sectionIds.map((sectionId) =>
              getSectionLaborEffortCorrections(projectId, sectionId).catch(
                () => [],
              ),
            ),
          ),
          Promise.all(
            ESTIMATE_CORRECTION_MODULES.map((module) =>
              getModuleLaborEffortCorrections(projectId, module).catch(
                () => [],
              ),
            ),
          ),
        ]);
        const fromApi = buildCorrectionLayersFromRecords([
          ...sectionLists.flat(),
          ...moduleLists.flat(),
        ]);
        const nextLayers =
          fromApi.length > 0
            ? fromApi
            : buildCorrectionLayersFromDetailed(currentDetails);
        if (!cancelled) {
          setCorrections((prev) =>
            ensureLayersForIncomeCorrections(
              preserveDraftCorrectionLayer(nextLayers, prev),
              incomeCorrectionsRef.current,
            ),
          );
        }
      } catch (err) {
        console.error("Ошибка загрузки корректировок:", err);
        if (!cancelled) {
          setCorrections((prev) =>
            ensureLayersForIncomeCorrections(
              buildCorrectionLayersFromDetailed(sectionDetailsRef.current),
              incomeCorrectionsRef.current,
            ),
          );
        }
      } finally {
        if (!cancelled) setCorrectionsLoading(false);
      }
    };

    loadCorrectionLayers();
    return () => {
      cancelled = true;
    };
  }, [projectId, sectionIdsKey, contextLoading]);

  // Синхронизируем по полям записей, а не по объекту сметы целиком:
  // иначе черновик суммы сбросится на каждый пересчёт шапки.
  useEffect(
    () => {
      if (!projectId || contextLoading) return undefined;

      const applyParsed = (payload) => {
        const parsed = parseIncomeCorrectionPayload(payload);
        setIncomeCorrections(parsed.items);
        setIncomeCorrectionTotal(parsed.total);
        setCorrections((prev) =>
          ensureLayersForIncomeCorrections(prev, parsed.items),
        );
      };

      const fromEstimate = parseIncomeCorrectionPayload(
        estimateData?.income_correction,
      );
      if (fromEstimate.items.length > 0 || fromEstimate.total) {
        applyParsed(estimateData.income_correction);
        return undefined;
      }

      let cancelled = false;
      getProjectIncomeCorrection(projectId, estimateData?.version)
        .then((data) => {
          if (cancelled) return;
          applyParsed(data);
        })
        .catch(() => {
          if (cancelled) return;
          setIncomeCorrections([]);
          setIncomeCorrectionTotal(null);
        });

      return () => {
        cancelled = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      projectId,
      contextLoading,
      incomeCorrectionSyncKey,
    ],
  );

  const persistDraftLayer = useCallback((updater) => {
    setCorrections((prev) =>
      typeof updater === "function" ? updater(prev) : updater,
    );
  }, []);

  const switchToWorkTab = useCallback(() => {
    setActiveBottomTab("work");
    if (projectId) {
      localStorage.setItem(`estimate_activeBottomTab_${projectId}`, "work");
    }
  }, [projectId]);

  const reloadEstimateAndCorrections = useCallback(async () => {
    await loadEstimateData(projectId);
  }, [loadEstimateData, projectId]);

  const handleAddCorrection = useCallback(() => {
    persistDraftLayer((prev) => {
      const last = prev[prev.length - 1];
      if (last && isEmptyCorrectionLayer(last)) {
        const hasIncome = Boolean(
          Number(
            findIncomeCorrectionForLayer(
              incomeCorrectionsRef.current,
              prev,
              last.number,
            )?.amount,
          ),
        );
        if (!hasIncome) return prev;
      }
      return [...prev, createCorrectionLayer(prev)];
    });
    setClientAmountDraft("");
    setClientAmountWithNds(false);
    setClientAmountNdsPercent(clampNdsPercent(headerData.nds_percent));
    switchToWorkTab();
  }, [persistDraftLayer, switchToWorkTab, headerData.nds_percent]);

  const handleCreateNewVersion = useCallback(async () => {
    if (!projectId || isCreatingCopy) return;
    if (
      !window.confirm(
        "Создать новую версию? Будет копия текущих ТЭП, сметы и КП без согласований. Согласованная версия останется в истории.",
      )
    ) {
      return;
    }

    try {
      setIsCreatingCopy(true);
      await restoreVersion(projectId, null);
      window.location.reload();
    } catch (error) {
      console.error("Ошибка при создании новой версии:", error);
      alert(`Ошибка при создании новой версии: ${error.message}`);
      setIsCreatingCopy(false);
    }
  }, [isCreatingCopy, projectId]);

  const persistIncomeCorrection = useCallback(
    async (amount, ndsIncludedInAmount, ndsPercent) => {
      if (!projectId || !currentCorrection) return;
      const nextAmount = Number(amount) || 0;
      const nextNds = Boolean(ndsIncludedInAmount);
      const nextPercent = clampNdsPercent(ndsPercent, headerData.nds_percent);
      const existing = findIncomeCorrectionForLayer(
        incomeCorrectionsRef.current,
        correctionsRef.current,
        currentCorrection.number,
      );
      const currentAmount = Number(existing?.amount) || 0;
      const currentNds = Boolean(existing?.nds_included_in_amount);
      const currentPercent = clampNdsPercent(
        existing?.nds_percent,
        headerData.nds_percent,
      );
      if (
        nextAmount === currentAmount &&
        nextNds === currentNds &&
        nextPercent === currentPercent
      ) {
        return;
      }
      if (!nextAmount && !existing?.id) return;

      const duplicates = (incomeCorrectionsRef.current || []).filter(
        (item) =>
          parseCorrectionLayerNumber(item.comment) ===
            currentCorrection.number && item.id != null,
      );
      const toDeleteIds = [
        ...new Set(
          [existing?.id, ...duplicates.map((item) => item.id)].filter(Boolean),
        ),
      ];

      setIncomeCorrectionSaving(true);
      try {
        await Promise.all(
          toDeleteIds.map((id) => deleteProjectIncomeCorrection(projectId, id)),
        );

        let saved = null;
        if (nextAmount) {
          saved = await saveProjectIncomeCorrection(projectId, {
            amount: nextAmount,
            nds_included_in_amount: nextNds,
            nds_percent: nextPercent,
            comment: correctionLayerComment(currentCorrection.number),
          });
        }

        setIncomeCorrections((prev) => {
          const next = prev.filter((item) => !toDeleteIds.includes(item.id));
          const added = saved
            ? parseIncomeCorrectionPayload(saved).items[0]
            : null;
          return added ? [...next, added] : next;
        });
        setIncomeCorrectionTotal(null);
      } catch (err) {
        setClientAmountDraft(formatClientAmountDraft(existing?.amount));
        setClientAmountWithNds(Boolean(existing?.nds_included_in_amount));
        setClientAmountNdsPercent(
          clampNdsPercent(existing?.nds_percent, headerData.nds_percent),
        );
        setErrorMessage(
          err?.message || "Не удалось сохранить корректировку дохода",
        );
        setShowErrorModal(true);
      } finally {
        setIncomeCorrectionSaving(false);
      }
    },
    [currentCorrection, headerData.nds_percent, projectId],
  );

  const commitCorrectionClientAmount = useCallback(
    (rawValue) => {
      if (!currentCorrection) return;
      const parsed = parseClientAmountInput(rawValue);
      const amount = parsed == null ? 0 : parsed;
      setClientAmountDraft(formatClientAmountDraft(amount));
      persistIncomeCorrection(
        amount,
        clientAmountWithNds,
        clientAmountNdsPercent,
      );
    },
    [
      clientAmountNdsPercent,
      clientAmountWithNds,
      currentCorrection,
      persistIncomeCorrection,
    ],
  );

  const handleClientAmountDraftChange = (e) => {
    const next = e.target.value;
    if (next !== "" && next !== "-" && !CLIENT_AMOUNT_INPUT_RE.test(next)) {
      return;
    }
    setClientAmountDraft(next);
  };

  const handleClientAmountWithNdsChange = (e) => {
    const checked = e.target.checked;
    setClientAmountWithNds(checked);
    const amount =
      parseClientAmountInput(clientAmountDraft) ??
      Number(currentLayerIncome?.amount) ??
      0;
    if (!amount) return;
    persistIncomeCorrection(amount, checked, clientAmountNdsPercent);
  };

  const commitCorrectionNdsPercent = (rawValue) => {
    const nextPercent = clampNdsPercent(rawValue, headerData.nds_percent);
    setClientAmountNdsPercent(nextPercent);
    const amount =
      parseClientAmountInput(clientAmountDraft) ??
      Number(currentLayerIncome?.amount) ??
      0;
    if (!amount) return;
    persistIncomeCorrection(amount, clientAmountWithNds, nextPercent);
  };

  const handleRestoreCorrection = useCallback(
    async (correctionId) => {
      const index = corrections.findIndex((item) => item.id === correctionId);
      if (index < 0) return;
      if (
        !window.confirm(
          "Откатить к этой корректировке? Поздние слои будут сняты, данные базовой сметы не изменятся.",
        )
      ) {
        return;
      }

      const workIdsToDelete = collectWorkCorrectionRecordIds(
        corrections.slice(index + 1),
      );
      const moduleIdsToDelete = collectModuleCorrectionRecordIds(
        corrections.slice(index + 1),
      );
      const removedLayers = corrections.slice(index + 1);
      const incomeIdsToDelete = removedLayers
        .map((layer) => incomeByLayer.get(layer.number)?.id)
        .filter((id) => id != null);
      try {
        setCorrectionsLoading(true);
        await Promise.all([
          ...workIdsToDelete.map((id) =>
            deleteLaborEffortCorrection(projectId, id),
          ),
          ...moduleIdsToDelete.map((id) =>
            deleteModuleLaborEffortCorrection(projectId, id),
          ),
          ...incomeIdsToDelete.map((id) =>
            deleteProjectIncomeCorrection(projectId, id),
          ),
        ]);
        persistDraftLayer(corrections.slice(0, index + 1));
        setShowCorrectionDrawer(false);
        switchToWorkTab();
        await reloadEstimateAndCorrections();
      } catch (err) {
        setErrorMessage(err?.message || "Не удалось откатить корректировки");
        setShowErrorModal(true);
      } finally {
        setCorrectionsLoading(false);
      }
    },
    [
      corrections,
      incomeByLayer,
      persistDraftLayer,
      projectId,
      reloadEstimateAndCorrections,
      switchToWorkTab,
    ],
  );

  const handleDeleteCorrection = useCallback(
    async (correctionId) => {
      const layer = corrections.find((item) => item.id === correctionId);
      if (!layer) return;
      if (
        !window.confirm(
          `Удалить корректировку ${layer.number} полностью? Будут сняты трудозатраты и сумма заказчику этого слоя. Данные базовой сметы не изменятся.`,
        )
      ) {
        return;
      }

      const workIdsToDelete = collectWorkCorrectionRecordIds([layer]);
      const moduleIdsToDelete = collectModuleCorrectionRecordIds([layer]);
      const incomeId = incomeByLayer.get(layer.number)?.id;
      try {
        setCorrectionsLoading(true);
        await Promise.all([
          ...workIdsToDelete.map((id) =>
            deleteLaborEffortCorrection(projectId, id),
          ),
          ...moduleIdsToDelete.map((id) =>
            deleteModuleLaborEffortCorrection(projectId, id),
          ),
          ...(incomeId != null
            ? [deleteProjectIncomeCorrection(projectId, incomeId)]
            : []),
        ]);
        persistDraftLayer((prev) =>
          prev.filter((item) => item.id !== correctionId),
        );
        if (incomeId != null) {
          setIncomeCorrections((prev) =>
            prev.filter((item) => item.id !== incomeId),
          );
        }
        switchToWorkTab();
        await reloadEstimateAndCorrections();
      } catch (err) {
        setErrorMessage(err?.message || "Не удалось удалить корректировку");
        setShowErrorModal(true);
      } finally {
        setCorrectionsLoading(false);
      }
    },
    [
      corrections,
      incomeByLayer,
      persistDraftLayer,
      projectId,
      reloadEstimateAndCorrections,
      switchToWorkTab,
    ],
  );

  const handleRestoreBaseEstimate = useCallback(async () => {
    if (
      !window.confirm("Откатить все корректировки и вернуться к базовой смете?")
    ) {
      return;
    }

    const workIdsToDelete = collectWorkCorrectionRecordIds(corrections);
    const moduleIdsToDelete = collectModuleCorrectionRecordIds(corrections);
    try {
      setCorrectionsLoading(true);
      const tasks = [
        ...workIdsToDelete.map((id) =>
          deleteLaborEffortCorrection(projectId, id),
        ),
        ...moduleIdsToDelete.map((id) =>
          deleteModuleLaborEffortCorrection(projectId, id),
        ),
      ];
      incomeCorrectionsRef.current.forEach((item) => {
        if (item?.id != null) {
          tasks.push(deleteProjectIncomeCorrection(projectId, item.id));
        }
      });
      await Promise.all(tasks);
      persistDraftLayer([]);
      setIncomeCorrections([]);
      setIncomeCorrectionTotal(null);
      setClientAmountDraft("");
      setShowCorrectionDrawer(false);
      switchToWorkTab();
      await reloadEstimateAndCorrections();
    } catch (err) {
      setErrorMessage(err?.message || "Не удалось удалить корректировки");
      setShowErrorModal(true);
    } finally {
      setCorrectionsLoading(false);
    }
  }, [
    corrections,
    persistDraftLayer,
    projectId,
    reloadEstimateAndCorrections,
    switchToWorkTab,
  ]);

  const updateCorrectionValue = useCallback(
    async (layerId, sectionId, blockKey, value) => {
      try {
        const isUnifiedSection = blockKey === UNIFIED_CORRECTION_KEY;
        const teiId = parseCorrectionTeiId(blockKey);
        if (!isUnifiedSection && teiId == null) {
          throw new Error(
            "Для этого раздела нет блока ТЭИ, корректировку нельзя сохранить на сервер",
          );
        }
        if (sectionId == null) {
          throw new Error("Не указан раздел для корректировки");
        }

        const layer = correctionsRef.current.find(
          (item) => item.id === layerId,
        );
        if (!layer) {
          throw new Error("Слой корректировки не найден");
        }

        const cellKey = correctionCellKey(sectionId, blockKey);
        const recordId = layer.recordIds?.[cellKey];
        const dayEfforts = Number(value) || 0;

        const createPayload = {
          day_efforts: dayEfforts,
          comment: correctionLayerComment(layer.number),
        };
        if (!isUnifiedSection) {
          createPayload.tei_id = teiId;
        }

        const saved = recordId
          ? await patchLaborEffortCorrection(projectId, recordId, {
              day_efforts: dayEfforts,
            })
          : await saveSectionLaborEffortCorrection(
              projectId,
              sectionId,
              createPayload,
            );

        persistDraftLayer((prev) =>
          prev.map((item) => {
            if (item.id !== layerId && item.number !== layer.number)
              return item;
            return {
              ...item,
              isDraft: false,
              values: {
                ...(item.values || {}),
                [cellKey]: Number(saved?.day_efforts) || dayEfforts,
              },
              recordIds: {
                ...(item.recordIds || {}),
                [cellKey]: saved?.id ?? recordId,
              },
              impacts: {
                ...(item.impacts || {}),
                [cellKey]: correctionImpactFromRecord(
                  saved,
                  item.impacts?.[cellKey],
                ),
              },
            };
          }),
        );
      } catch (err) {
        setErrorMessage(err?.message || "Не удалось сохранить корректировку");
        setShowErrorModal(true);
        throw err;
      }
    },
    [persistDraftLayer, projectId],
  );

  const updateModuleCorrectionValue = useCallback(
    async (layerId, module, role, sectionId, value) => {
      try {
        if (!module || !role) {
          throw new Error("Не указаны модуль или роль для корректировки");
        }

        const layer = correctionsRef.current.find(
          (item) => item.id === layerId,
        );
        if (!layer) {
          throw new Error("Слой корректировки не найден");
        }

        const cellKey = moduleCorrectionCellKey(module, role, sectionId);
        const recordId = layer.recordIds?.[cellKey];
        const dayEfforts = Number(value) || 0;
        const payload = {
          role,
          day_efforts: dayEfforts,
          comment: correctionLayerComment(layer.number),
        };
        if (sectionId != null && sectionId !== "") {
          payload.section_id = Number(sectionId);
        }

        const saved = recordId
          ? await patchModuleLaborEffortCorrection(projectId, recordId, {
              day_efforts: dayEfforts,
            })
          : await saveModuleLaborEffortCorrection(projectId, module, payload);

        persistDraftLayer((prev) =>
          prev.map((item) => {
            if (item.id !== layerId && item.number !== layer.number)
              return item;
            return {
              ...item,
              isDraft: false,
              values: {
                ...(item.values || {}),
                [cellKey]: Number(saved?.day_efforts) || dayEfforts,
              },
              recordIds: {
                ...(item.recordIds || {}),
                [cellKey]: saved?.id ?? recordId,
              },
              impacts: {
                ...(item.impacts || {}),
                [cellKey]: correctionImpactFromRecord(
                  saved,
                  item.impacts?.[cellKey],
                ),
              },
            };
          }),
        );
      } catch (err) {
        setErrorMessage(err?.message || "Не удалось сохранить корректировку");
        setShowErrorModal(true);
        throw err;
      }
    },
    [persistDraftLayer, projectId],
  );

  // удалены manageTable, manageColumns, handleExpandRow

  // Функция для сохранения данных со всех вкладок
  const persistEstimateFromTabs = async () => {
    if (workTabRef.current && workTabRef.current.deleteSectionsBeforeSave) {
      await workTabRef.current.deleteSectionsBeforeSave();
    }

    if (workTabRef.current && workTabRef.current.syncDataBeforeSave) {
      workTabRef.current.syncDataBeforeSave();
    }

    const synced = contractSectionsTabRef.current?.syncDataBeforeSave?.();
    const contractSectionsSnapshot = Array.isArray(synced?.sections)
      ? synced.sections
      : contractSectionsTabRef.current?.getContractSections?.() || [];

    const saveResult = await performSave(projectId);
    return { saveResult, contractSectionsSnapshot };
  };

  // Функция для сохранения данных
  const handleSave = async () => {
    try {
      setSaving(true);

      await persistEstimateFromTabs();

      // Показываем уведомление об успешном сохранении
      setSuccessMessage("Данные успешно сохранены!");
      setShowSuccessModal(true);

      // Даем время попапу показаться перед сбросом состояния
      setTimeout(() => {
        setSaving(false);
      }, 100);
    } catch (error) {
      setErrorMessage("Ошибка при сохранении данных: " + error.message);
      setShowErrorModal(true);
      setSaving(false);
    }
  };

  // Обработчик отправки сметы на согласование
  const handleApprovalSubmit = async (selectedApprovers, comment) => {
    if (!projectId || !headerData) {
      alert("Ошибка: отсутствуют данные проекта или сметы");
      return;
    }

    setIsSubmittingApproval(true);
    setSaving(true);
    try {
      const { saveResult, contractSectionsSnapshot } =
        await persistEstimateFromTabs();
      if (saveResult == null) {
        throw new Error(
          "Не удалось сохранить смету перед отправкой на согласование",
        );
      }

      const totalPrice = headerData.contract_value || 0;

      console.log("Отправка сметы на согласование:", {
        projectId,
        approverIds: selectedApprovers,
        totalPrice,
        comment,
      });

      const result = await requestProjectEstimateApproval(
        projectId,
        selectedApprovers,
        totalPrice,
        comment,
      );

      console.log("Результат отправки на согласование:", result);

      // Показываем успешное сообщение
      setSuccessMessage("Смета успешно отправлена на согласование!");
      setShowSuccessModal(true);

      // Закрываем модальное окно
      setShowApprovalModal(false);

      // Обновляем статусы согласования
      const statuses = await getProjectApprovalStatuses(parseInt(projectId));
      setApprovalStatuses(statuses);

      // Перезагружаем данные сметы, чтобы обновился флаг editable.
      // Если GET после согласования пришёл без разделов на договоре — оставляем только что сохранённые.
      await loadEstimateData(projectId, null, {
        preserveContractSectionsIfEmpty: contractSectionsSnapshot,
        silent: true,
      });
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);
      setErrorMessage(`Ошибка при отправке на согласование: ${error.message}`);
      setShowErrorModal(true);
    } finally {
      setIsSubmittingApproval(false);
      setSaving(false);
    }
  };

  // Обработчик отмены согласования
  const handleCancelApproval = async () => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите отозвать отправку на согласование?",
      )
    ) {
      return;
    }

    try {
      setIsCancellingApproval(true);
      await cancelApprovalRequest(parseInt(projectId));

      // Обновляем статусы согласования
      const statuses = await getProjectApprovalStatuses(parseInt(projectId));
      setApprovalStatuses(statuses);

      // Перезагружаем данные сметы, чтобы обновился флаг editable
      await loadEstimateData(projectId);

      // Показываем успешное сообщение
      setSuccessMessage("Отправка на согласование успешно отозвана!");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Ошибка при отмене согласования:", error);
      setErrorMessage(`Ошибка при отмене согласования: ${error.message}`);
      setShowErrorModal(true);
    } finally {
      setIsCancellingApproval(false);
    }
  };

  const reloadCorrectionApprovals = useCallback(async () => {
    if (!projectId) return [];
    try {
      const approvals = await getProjectCorrectionApprovals(
        parseInt(projectId),
      );
      setCorrectionApprovals(approvals || []);
      return approvals || [];
    } catch (error) {
      console.error("Ошибка при загрузке согласований корректировок:", error);
      setCorrectionApprovals([]);
      return [];
    }
  }, [projectId]);

  const handleCorrectionApprovalSubmit = async (selectedApprovers, comment) => {
    if (!projectId || !headerData) {
      alert("Ошибка: отсутствуют данные проекта или сметы");
      return;
    }

    setIsSubmittingCorrectionApproval(true);
    setSaving(true);
    try {
      const billing = incomeCorrectionListTotals(
        incomeCorrections,
        headerData.nds_percent,
        incomeCorrectionTotal,
      );
      const totalPrice = billing.contract || 0;
      await requestCorrectionsApproval(
        parseInt(projectId),
        selectedApprovers,
        totalPrice,
        comment,
      );

      setSuccessMessage("Корректировки успешно отправлены на согласование!");
      setShowSuccessModal(true);
      setShowCorrectionApprovalModal(false);
      await reloadCorrectionApprovals();
    } catch (error) {
      console.error(
        "Ошибка при отправке корректировок на согласование:",
        error,
      );
      setErrorMessage(
        `Ошибка при отправке корректировок на согласование: ${error.message}`,
      );
      setShowErrorModal(true);
    } finally {
      setIsSubmittingCorrectionApproval(false);
      setSaving(false);
    }
  };

  const handleCancelCorrectionApproval = async () => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите отозвать отправку корректировок на согласование?",
      )
    ) {
      return;
    }

    try {
      setIsCancellingCorrectionApproval(true);
      await cancelCorrectionsApprovalRequest(parseInt(projectId));
      await reloadCorrectionApprovals();
      setSuccessMessage("Отправка корректировок на согласование отозвана!");
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Ошибка при отмене согласования корректировок:", error);
      setErrorMessage(
        `Ошибка при отмене согласования корректировок: ${error.message}`,
      );
      setShowErrorModal(true);
    } finally {
      setIsCancellingCorrectionApproval(false);
    }
  };

  const getStatusIcon = (status) => {
    const kind = getApprovalStatusKind(status);
    if (kind === "approved") {
      return <span className="estimate-approval-status-icon approved">✓</span>;
    }
    if (kind === "rejected") {
      return <span className="estimate-approval-status-icon rejected">✕</span>;
    }
    if (kind === "cancelled") {
      return <span className="estimate-approval-status-icon cancelled">✕</span>;
    }
    return <span className="estimate-approval-status-icon pending">⏳</span>;
  };

  const getStatusText = (status) => {
    const kind = getApprovalStatusKind(status);
    if (kind === "approved") return "Согласован";
    if (kind === "rejected") return "Отклонен";
    if (kind === "cancelled") return "Отменен инициатором";
    return "На рассмотрении";
  };

  const currentEstimateVersion = estimateData?.version;
  const estimateApprovalsForVersion = approvalsForEstimateVersion(
    approvalStatuses,
    currentEstimateVersion,
  );

  // Находим активное согласование (только то, которое еще не завершено)
  // Ищем согласование, которое еще не завершено (есть шаги со статусом "pending" или "на рассмотрении")
  // Также проверяем общий статус согласования
  const activeApproval = estimateApprovalsForVersion.find((approval) => {
    // Проверяем общий статус согласования - исключаем завершенные и отмененные
    const status = approval.status?.toLowerCase() || "";
    if (
      status === "согласован" ||
      status === "отклонен" ||
      status === "отменен инициатором" ||
      status === "не согласован"
    ) {
      return false; // Согласование уже завершено или отменено
    }

    // Если статус "на рассмотрении" - это активное согласование
    if (status === "на рассмотрении") {
      return true;
    }

    // Если есть шаги, проверяем их статусы
    if (approval.steps && approval.steps.length > 0) {
      // Проверяем, есть ли хотя бы один шаг, который еще не завершен
      return approval.steps.some((step) => {
        const stepStatus = step.status?.toLowerCase() || "";
        return (
          stepStatus === "pending" ||
          stepStatus === "на рассмотрении" ||
          stepStatus === "" ||
          step.status === null ||
          step.status === undefined
        );
      });
    }

    // Если шагов нет, но статус не завершен, считаем активным
    return !approval.status || status === "";
  });

  const latestInitiatorApproval =
    [...estimateApprovalsForVersion]
      .filter((approval) => userData && approval.initiator_id === userData.id)
      .sort(
        (a, b) =>
          Number(b.version ?? b.id ?? 0) - Number(a.version ?? a.id ?? 0),
      )[0] || null;

  const displayedApproval = activeApproval || latestInitiatorApproval;
  const displayedApprovalKind = getApprovalStatusKind(
    displayedApproval?.status,
  );
  const rejectionRemarks = (displayedApproval?.steps || [])
    .filter((step) => step.comment?.trim())
    .map((step) => ({
      author: step.approver_name || "Согласующий",
      comment: step.comment.trim(),
    }));

  const isInitiator =
    displayedApproval &&
    userData &&
    displayedApproval.initiator_id === userData.id;

  const isCurrentVersionLocked = isEstimateLockedByApprovals(
    currentEstimateVersion,
    approvalStatuses,
    estimateApprovals,
  );
  const isCurrentVersionApproved = isEstimateVersionApproved(
    currentEstimateVersion,
    approvalStatuses,
    estimateApprovals,
  );

  const showApprovalStatus =
    Boolean(displayedApproval) &&
    isInitiator &&
    !isReadOnly &&
    (displayedApprovalKind !== "approved" || isCurrentVersionApproved);
  const showCancelButton =
    Boolean(activeApproval) &&
    activeApproval?.id === displayedApproval?.id &&
    isInitiator &&
    !isReadOnly &&
    displayedApprovalKind === "pending";

  // Отладочная информация
  // console.log("Отладка согласования:", {
  //   approvalStatuses: approvalStatuses.map((a) => ({
  //     id: a.id,
  //     status: a.status,
  //     initiator_id: a.initiator_id,
  //     steps: a.steps?.map((s) => ({ id: s.id, status: s.status })),
  //   })),
  //   activeApproval: activeApproval
  //     ? {
  //         id: activeApproval.id,
  //         status: activeApproval.status,
  //         initiator_id: activeApproval.initiator_id,
  //       }
  //     : null,
  //   isInitiator,
  //   hasActiveApproval,
  //   showApprovalControls,
  //   isReadOnly,
  //   userData: userData?.id,
  // });

  // Согласованную или отправленную на согласование версию нельзя править,
  // даже если GET вернул editable: true. Новая копия (version > max approval) снова открыта.
  const canManageCorrections = !isReadOnly && isCurrentVersionApproved;

  const hasPersistedCorrections = corrections.some((layer) => {
    if (!isEmptyCorrectionLayer(layer)) return true;
    return Boolean(Number(incomeByLayer.get(layer.number)?.amount));
  });
  const correctionApprovalsForVersion = approvalsForEstimateVersion(
    correctionApprovals,
    currentEstimateVersion,
  );
  const activeCorrectionApproval = findActiveApproval(
    correctionApprovalsForVersion,
  );
  const latestCorrectionApproval =
    [...correctionApprovalsForVersion].sort(
      (a, b) => Number(b.id ?? 0) - Number(a.id ?? 0),
    )[0] || null;
  const latestInitiatorCorrectionApproval =
    [...correctionApprovalsForVersion]
      .filter((approval) => userData && approval.initiator_id === userData.id)
      .sort(
        (a, b) =>
          Number(b.version ?? b.id ?? 0) - Number(a.version ?? a.id ?? 0),
      )[0] || null;
  const displayedCorrectionApproval =
    activeCorrectionApproval || latestInitiatorCorrectionApproval;
  const displayedCorrectionApprovalKind = getApprovalStatusKind(
    displayedCorrectionApproval?.status,
  );
  const correctionRejectionRemarks = (displayedCorrectionApproval?.steps || [])
    .filter((step) => step.comment?.trim())
    .map((step) => ({
      author: step.approver_name || "Согласующий",
      comment: step.comment.trim(),
    }));
  const isCorrectionApprovalInitiator =
    Boolean(displayedCorrectionApproval) &&
    userData &&
    displayedCorrectionApproval.initiator_id === userData.id;
  const isCorrectionsPending =
    Boolean(activeCorrectionApproval) &&
    displayedCorrectionApprovalKind === "pending";
  const showCorrectionApprovalStatus =
    Boolean(displayedCorrectionApproval) &&
    isCorrectionApprovalInitiator &&
    !isReadOnly &&
    (hasPersistedCorrections || isCorrectionsPending);
  const isLatestCorrectionApproved =
    hasPersistedCorrections &&
    getApprovalStatusKind(latestCorrectionApproval?.status) === "approved";
  const currentCorrectionRecordCount =
    corrections.reduce(
      (sum, layer) => sum + Object.keys(layer.recordIds || {}).length,
      0,
    ) + incomeCorrections.filter((item) => Number(item?.amount)).length;
  const latestApprovedCorrectionCount = latestCorrectionApproval
    ? (Number(latestCorrectionApproval.labor_effort_corrections_count) || 0) +
      (Number(latestCorrectionApproval.module_corrections_count) || 0) +
      (Number(latestCorrectionApproval.income_corrections_count) || 0)
    : 0;
  const hasNewCorrectionsSinceApproval =
    isLatestCorrectionApproved &&
    (Boolean(currentCorrection?.isDraft) ||
      currentCorrectionRecordCount > latestApprovedCorrectionCount);
  const canAddCorrections = canManageCorrections && !isCorrectionsPending;
  const canEditCurrentCorrection =
    canAddCorrections &&
    (!isLatestCorrectionApproved || hasNewCorrectionsSinceApproval);
  const areCorrectionsFrozen =
    isCorrectionsPending ||
    (isLatestCorrectionApproved && !hasNewCorrectionsSinceApproval);
  const currentLayerHasPersistedData = Boolean(
    currentCorrection &&
    (!isEmptyCorrectionLayer(currentCorrection) ||
      Boolean(Number(currentLayerIncome?.amount))),
  );
  const canSendCorrectionApproval =
    canAddCorrections &&
    hasPersistedCorrections &&
    (!isLatestCorrectionApproved ||
      (hasNewCorrectionsSinceApproval && currentLayerHasPersistedData));
  const showCancelCorrectionButton =
    Boolean(activeCorrectionApproval) &&
    activeCorrectionApproval?.id === displayedCorrectionApproval?.id &&
    isCorrectionApprovalInitiator &&
    !isReadOnly &&
    displayedCorrectionApprovalKind === "pending";
  const showCorrectionBanner = Boolean(currentCorrection);

  const effectiveIsEditable =
    !isReadOnly &&
    !projectLockedByApproval &&
    estimateData?.editable !== false &&
    !isCurrentVersionLocked;

  const correctionTotals = useMemo(
    () => sumAllCorrectionImpact(corrections),
    [corrections],
  );
  const correctionExtraMoney = correctionImpactMoney(correctionTotals);
  const correctionClientBilling = useMemo(
    () =>
      incomeCorrectionListTotals(
        incomeCorrections,
        headerData.nds_percent,
        incomeCorrectionTotal,
      ),
    [incomeCorrections, headerData.nds_percent, incomeCorrectionTotal],
  );
  const correctionClientIncome = correctionClientBilling.income;
  const correctionClientContract = correctionClientBilling.contract;
  const correctionClientNds = correctionClientBilling.nds;
  const hasCorrectionMoneyChange = correctionExtraMoney !== 0;
  const hasCorrectionIncomeChange = correctionClientIncome !== 0;
  const hasCorrectionContractChange = correctionClientContract !== 0;
  const correctionMoneyDeltaLabel = formatSignedDelta(correctionExtraMoney, {
    money: true,
  });
  const correctionIncomeDeltaLabel = formatSignedDelta(correctionClientIncome, {
    money: true,
  });
  const correctionContractDeltaLabel = formatSignedDelta(
    correctionClientContract,
    { money: true },
  );
  const indirectPercent = Number(headerData.indirect_expenditures_percent) || 0;
  const correctionExtraIndirect =
    (correctionClientContract * indirectPercent) / 100;
  const hasCorrectionIndirectChange = correctionExtraIndirect !== 0;
  const correctionIndirectDeltaLabel = formatSignedDelta(
    correctionExtraIndirect,
    { money: true },
  );
  const netProfitDelta =
    correctionClientIncome - correctionExtraMoney - correctionExtraIndirect;
  const hasNetProfitCorrectionChange = netProfitDelta !== 0;
  const billedRecordsCount = [...incomeByLayer.values()].filter((item) =>
    Number(item?.amount),
  ).length;

  // Данные для правого блока (без клиентских вычислений — всё отдаёт бэкенд)
  const profitData = {
    total: (headerData.contract_value || 0) + correctionClientContract,
    // Доход = сумма договора без НДС + суммы заказчику по корректировкам
    income:
      (headerData.contract_value_before_nds || 0) + correctionClientIncome,
    incomePercent: 100,
    // Себестоимость-план
    cost: (headerData.cost_price || 0) + correctionExtraMoney,
    costPercent:
      headerData.cost_price_percent !== undefined &&
      headerData.cost_price_percent !== null
        ? Number(headerData.cost_price_percent)
        : 0,
    // Косвенные расходы: та же доля %, прирост от суммы договора по корректировке
    expense: (headerData.indirect_expenditures || 0) + correctionExtraIndirect,
    expensePercent: indirectPercent,
    // Чистая прибыль
    netProfit:
      (headerData.net_profit || 0) -
      correctionExtraMoney +
      correctionClientIncome -
      correctionExtraIndirect,
    netProfitPercent:
      headerData.net_profit_percent !== undefined &&
      headerData.net_profit_percent !== null
        ? Number(headerData.net_profit_percent)
        : 0,
  };

  const ndsPercent = Number(headerData.nds_percent) || 0;
  const ndsPercentLabel = Number.isInteger(ndsPercent)
    ? String(ndsPercent)
    : ndsPercent.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  const expensePercentLabel = profitData.expensePercent.toLocaleString(
    "ru-RU",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  );

  // Данные для блока сумм
  const sums = [
    {
      value: (headerData.contract_value || 0) + correctionClientContract,
      label:
        ndsPercent > 0
          ? `Сумма основного договора с НДС ${ndsPercentLabel}%`
          : "Сумма основного договора без НДС",
      className: "main",
    },
    {
      value:
        (headerData.contract_value_before_nds || 0) + correctionClientIncome,
      label: `Сумма за вычетом НДС ${headerData.nds_percent || 0}%`,
      className: "gray",
      ndsValue: (headerData.nds_value || 0) + correctionClientNds,
    },
    {
      value: headerData.contract_value_construction || 0,
      label: `На разработку ${
        headerData.contract_value_construction_percent || 0
      }%`,
      className: "blue",
    },
    {
      value: (headerData.cost_price || 0) + correctionExtraMoney,
      label: "Себестоимость",
      className: "green",
    },
  ];

  const deviation =
    (headerData.difference || 0) -
    correctionExtraMoney +
    correctionClientIncome;
  const aupAhoBonusAmount = Number(headerData.bonus_aup_aho) || 0;
  const aupAhoBonusPercentValue = Number(headerData.bonus_percent_aup_aho) || 0;
  const showAupAhoBonusRow =
    aupAhoBonusAmount !== 0 || aupAhoBonusPercentValue > 0;
  const deviationColor = deviation < 0 ? "red" : deviation > 0 ? "green" : "";
  const canEditDevelopmentPercent = effectiveIsEditable;

  const handleDevelopmentPercentChange = (e) => {
    const value = e.target.value.replace(",", ".");
    if (!/^\d*\.?\d*$/.test(value)) return;
    setDevelopmentPercentDraft(value);
  };

  const handleDevelopmentPercentFocus = (e) => {
    e.target.select();
    setIsDevelopmentPercentEditing(true);
  };

  const handleDevelopmentPercentCancel = () => {
    setDevelopmentPercentDraft(developmentPercentApplied);
    setIsDevelopmentPercentEditing(false);
  };

  const handleDevelopmentPercentApply = async () => {
    if (!projectId || !estimateData) return;

    const normalizedPercent = formatPercentDisplay(
      parsePercentValue(developmentPercentDraft),
    );
    const appliedPercent = parsePercentValue(developmentPercentApplied);
    const nextPercent = parsePercentValue(normalizedPercent);

    if (nextPercent === appliedPercent) {
      setIsDevelopmentPercentEditing(false);
      return;
    }

    setIsDevelopmentPercentSubmitting(true);
    try {
      await updateThresholdCostPercent(projectId, nextPercent);
      await loadEstimateData(projectId);

      setDevelopmentPercentApplied(normalizedPercent);
      setDevelopmentPercentDraft(normalizedPercent);
      setIsDevelopmentPercentEditing(false);
    } catch (err) {
      console.error("Ошибка обновления процента 'На разработку':", err);
      setErrorMessage(
        err?.message || "Не удалось пересчитать смету после изменения процента",
      );
      setShowErrorModal(true);
    } finally {
      setIsDevelopmentPercentSubmitting(false);
    }
  };

  // Создаем значение контекста
  const contextValue = {
    projectId,
    updateHeaderData,
    headerData,
    // Добавляем данные для всех вкладок
    bimCoordinationData: headerData.bim_coordination || {},
    qualityControlData: headerData.quality_control || {},
    projectDeliveryData: headerData.project_delivery || {},
    projectManagementData: headerData.project_management || {},
    // Пробросим суммарные проценты из WorkTab через headerData.total_construction_design
    percentTotal:
      headerData.total_construction_design?.expenditures_percent?.threshold,
    // Флаг редактируемости сметы
    isEditable: effectiveIsEditable,
    corrections,
    updateCorrectionValue,
    updateModuleCorrectionValue,
    isCorrectionMode: corrections.length > 0,
    canManageCorrections: canAddCorrections,
    canEditCorrections: canEditCurrentCorrection,
  };

  // Компонент модала успеха
  const SuccessModal = useMemo(() => {
    const Modal = ({ open, onClose, message }) => {
      if (!open) return null;

      return (
        <>
          <div className="approval-modal-backdrop" onClick={onClose} />
          <div className="approval-modal">
            <div className="approval-modal-title">Успешно!</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#f0f8f4",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#226a3a",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>✅</span>
              <span>{message}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn green"
                onClick={onClose}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      );
    };

    return Modal;
  }, []); // Пустой массив зависимостей, так как компонент не зависит от внешних переменных

  // Компонент модала ошибки
  const ErrorModal = useMemo(() => {
    const Modal = ({ open, onClose, message }) => {
      if (!open) return null;

      return (
        <>
          <div className="approval-modal-backdrop" onClick={onClose} />
          <div className="approval-modal">
            <div className="approval-modal-title">Ошибка</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#fff8f1",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#b26a00",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>⚠️</span>
              <span>{message}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn outline"
                onClick={onClose}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      );
    };

    return Modal;
  }, []); // Пустой массив зависимостей

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return <div>Ошибка загрузки данных: {error}</div>;
  }

  // Если данные еще не загружены, показываем прелоадер
  if (!headerData || !estimateData) {
    return <Preloader />;
  }

  return (
    <EstimateContext.Provider value={contextValue}>
      <div className={`estimate-container ${saving ? "saving" : ""}`}>
        {showApprovalStatus && (
          <div
            className={`estimate-approval-panel${
              displayedApprovalKind === "rejected"
                ? " estimate-approval-panel--rejected"
                : displayedApprovalKind === "cancelled"
                  ? " estimate-approval-panel--cancelled"
                  : displayedApprovalKind === "approved"
                    ? " estimate-approval-panel--approved"
                    : ""
            }`}
          >
            <div className="estimate-approval-panel__title">
              {displayedApprovalKind === "rejected"
                ? "Согласование отклонено"
                : displayedApprovalKind === "cancelled"
                  ? "Согласование отменено инициатором"
                  : displayedApprovalKind === "approved"
                    ? "Смета согласована"
                    : "Смета на согласовании"}
            </div>
            {displayedApproval.comment?.trim() && (
              <div className="estimate-approval-comment">
                <span className="estimate-approval-comment-label">
                  Описание:
                </span>
                <span>{displayedApproval.comment}</span>
              </div>
            )}
            {(displayedApproval.steps || []).map((step, index) => (
              <div key={step.id || index} className="estimate-approval-step">
                <span className="estimate-approver-name">
                  {step.approver_name || "Не указан"}:
                </span>
                {getStatusIcon(step.status)}
                <span className="estimate-step-status-text">
                  {getStatusText(step.status)}
                </span>
                {step.comment?.trim() &&
                  getApprovalStatusKind(step.status) !== "rejected" && (
                    <span className="estimate-step-comment">
                      ({step.comment})
                    </span>
                  )}
              </div>
            ))}
            {displayedApprovalKind === "rejected" && (
              <div className="estimate-approval-remarks">
                <div className="estimate-approval-remarks__label">
                  Замечание
                </div>
                {rejectionRemarks.length > 0 ? (
                  rejectionRemarks.map((remark, index) => (
                    <div
                      key={`${remark.author}-${index}`}
                      className="estimate-approval-remarks__item"
                    >
                      <span className="estimate-approval-remarks__author">
                        {remark.author}:
                      </span>{" "}
                      {remark.comment}
                    </div>
                  ))
                ) : (
                  <div className="estimate-approval-remarks__item">
                    Согласующий не указал комментарий
                  </div>
                )}
              </div>
            )}
            {showCancelButton && (
              <button
                className="estimate-cancel-approval-btn"
                onClick={handleCancelApproval}
                disabled={isCancellingApproval || saving}
              >
                {isCancellingApproval ? "Отзыв..." : "Отозвать отправку"}
              </button>
            )}
          </div>
        )}
        {showCorrectionApprovalStatus && (
          <div
            className={`estimate-approval-panel${
              displayedCorrectionApprovalKind === "rejected"
                ? " estimate-approval-panel--rejected"
                : displayedCorrectionApprovalKind === "cancelled"
                  ? " estimate-approval-panel--cancelled"
                  : displayedCorrectionApprovalKind === "approved"
                    ? " estimate-approval-panel--approved"
                    : ""
            }`}
          >
            <div className="estimate-approval-panel__title">
              {displayedCorrectionApprovalKind === "rejected"
                ? "Согласование корректировок отклонено"
                : displayedCorrectionApprovalKind === "cancelled"
                  ? "Согласование корректировок отменено инициатором"
                  : displayedCorrectionApprovalKind === "approved"
                    ? "Корректировки согласованы"
                    : "Корректировки на согласовании"}
            </div>
            <div className="estimate-approval-comment">
              <span className="estimate-approval-comment-label">
                Сумма заказчику:
              </span>
              <span>
                {formatCorrectionApprovalAmount(
                  displayedCorrectionApproval.total_price,
                )}
              </span>
            </div>
            {displayedCorrectionApproval.comment?.trim() && (
              <div className="estimate-approval-comment">
                <span className="estimate-approval-comment-label">
                  Комментарий:
                </span>
                <span>{displayedCorrectionApproval.comment}</span>
              </div>
            )}
            {(displayedCorrectionApproval.steps || []).map((step, index) => (
              <div key={step.id || index} className="estimate-approval-step">
                <span className="estimate-approver-name">
                  {step.approver_name || "Не указан"}:
                </span>
                {getStatusIcon(step.status)}
                <span className="estimate-step-status-text">
                  {getStatusText(step.status)}
                </span>
                {step.comment?.trim() &&
                  getApprovalStatusKind(step.status) !== "rejected" && (
                    <span className="estimate-step-comment">
                      ({step.comment})
                    </span>
                  )}
              </div>
            ))}
            {displayedCorrectionApprovalKind === "rejected" && (
              <div className="estimate-approval-remarks">
                <div className="estimate-approval-remarks__label">
                  Замечание
                </div>
                {correctionRejectionRemarks.length > 0 ? (
                  correctionRejectionRemarks.map((remark, index) => (
                    <div
                      key={`${remark.author}-${index}`}
                      className="estimate-approval-remarks__item"
                    >
                      <span className="estimate-approval-remarks__author">
                        {remark.author}:
                      </span>{" "}
                      {remark.comment}
                    </div>
                  ))
                ) : (
                  <div className="estimate-approval-remarks__item">
                    Согласующий не указал комментарий
                  </div>
                )}
              </div>
            )}
            {showCancelCorrectionButton && (
              <button
                className="estimate-cancel-approval-btn"
                onClick={handleCancelCorrectionApproval}
                disabled={isCancellingCorrectionApproval || saving}
              >
                {isCancellingCorrectionApproval
                  ? "Отзыв..."
                  : "Отозвать отправку корректировок"}
              </button>
            )}
          </div>
        )}
        <div className="estimate-header-row">
          <div className="estimate-header-left">
            <div className="estimate-inputs-block estimate-inputs-row">
              <div className="estimate-input-group">
                <label>Введите № заявки на договор</label>
                <input
                  value={contractNumber}
                  onChange={(e) => setContractNumber(e.target.value)}
                  placeholder="Введите номер заявки"
                  disabled={!effectiveIsEditable}
                />
              </div>
              <div className="estimate-input-group">
                <label>Введите дату заявки на договор</label>
                <DatePicker
                  selected={contractDate}
                  onChange={(date) => setContractDate(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Выберите дату"
                  locale="ru"
                  calendarStartDay={1}
                  className="estimate-datepicker"
                  popperClassName="estimate-datepicker-popper"
                  disabled={!effectiveIsEditable}
                />
              </div>
            </div>
          </div>
          <div className="estimate-version-badge-container">
            {estimateData?.version !== undefined &&
              estimateData?.version !== null && (
                <span className="estimate-version-badge">
                  Версия {estimateData.version}
                </span>
              )}
            {showCorrectionBanner && (
              <span className="estimate-correction-badge">
                Корректировка {currentCorrection.number}
              </span>
            )}
            {canAddCorrections && (
              <button
                className="estimate-correction-btn"
                onClick={handleAddCorrection}
                disabled={saving || correctionsLoading}
                title="Корректировку можно создать только для согласованной сметы"
              >
                {correctionsLoading ? "Загрузка..." : "Добавить корректировку"}
              </button>
            )}
            <button
              className="estimate-history-btn"
              onClick={() => setShowCorrectionDrawer(true)}
              disabled={saving}
            >
              История корректировок
            </button>
            {!isReadOnly && !effectiveIsEditable && (
              <button
                className="estimate-new-version-btn"
                onClick={handleCreateNewVersion}
                disabled={saving || isCreatingCopy}
                title="Создать редактируемую копию текущей сметы"
              >
                {isCreatingCopy ? "Создание версии..." : "Создать новую версию"}
              </button>
            )}
            <button
              className="estimate-history-btn"
              onClick={() => setShowVersionDrawer(true)}
              disabled={saving}
              style={{
                opacity: saving ? 0.6 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              <span className="estimate-history-icon">⟳</span> История версий
            </button>
          </div>
        </div>
        {showCorrectionBanner && (
          <div className="estimate-correction-banner">
            <div className="estimate-correction-banner-body">
              <div className="estimate-correction-banner-text">
                Режим корректировки {currentCorrection.number}. Базовая смета
                заблокирована: ставка и доли наследуются, сумма заказчику
                относится к этой корректировке.
                {isCorrectionsPending
                  ? " Корректировки отправлены на согласование и временно недоступны для редактирования."
                  : areCorrectionsFrozen
                    ? " Согласованные корректировки зафиксированы. Чтобы внести новые изменения, добавьте новую корректировку."
                    : ""}
              </div>
              <div className="estimate-correction-banner-summary">
                <div className="estimate-correction-banner-summary-item">
                  <span className="estimate-correction-banner-summary-label">
                    Сумма заказчику
                  </span>
                  <span className="estimate-correction-banner-summary-value">
                    {formatCompareMoney(correctionClientContract)}
                  </span>
                </div>
                {displayedCorrectionApproval?.comment?.trim() ? (
                  <div className="estimate-correction-banner-summary-item estimate-correction-banner-summary-item--comment">
                    <span className="estimate-correction-banner-summary-label">
                      Комментарий
                    </span>
                    <span className="estimate-correction-banner-summary-value">
                      {displayedCorrectionApproval.comment}
                    </span>
                  </div>
                ) : null}
              </div>
              {corrections.length > 1 && (
                <ul className="estimate-correction-billing-list">
                  {corrections.slice(0, -1).map((layer) => {
                    const income = incomeByLayer.get(layer.number);
                    return (
                      <li key={layer.id}>
                        <span>
                          Корректировка {layer.number}:{" "}
                          {formatIncomeCorrectionAmount(income)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="estimate-correction-billing">
                <div className="estimate-correction-billing-field">
                  <label htmlFor="correction-client-amount">
                    Сумма заказчику
                  </label>
                  <input
                    id="correction-client-amount"
                    type="text"
                    inputMode="decimal"
                    className="estimate-correction-billing-input"
                    value={clientAmountDraft}
                    onChange={handleClientAmountDraftChange}
                    onBlur={() =>
                      commitCorrectionClientAmount(clientAmountDraft)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitCorrectionClientAmount(clientAmountDraft);
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder="0"
                    disabled={
                      saving ||
                      correctionsLoading ||
                      incomeCorrectionSaving ||
                      !canEditCurrentCorrection
                    }
                  />
                </div>
                <div className="estimate-correction-billing-field">
                  <label htmlFor="correction-nds-percent">Ставка НДС (%)</label>
                  <input
                    id="correction-nds-percent"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    className="estimate-correction-billing-input estimate-correction-billing-input--nds"
                    value={clientAmountNdsPercent}
                    onChange={(e) =>
                      setClientAmountNdsPercent(
                        clampNdsPercent(e.target.value, 0),
                      )
                    }
                    onBlur={(e) => commitCorrectionNdsPercent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitCorrectionNdsPercent(e.currentTarget.value);
                        e.currentTarget.blur();
                      }
                    }}
                    disabled={
                      saving ||
                      correctionsLoading ||
                      incomeCorrectionSaving ||
                      !canEditCurrentCorrection
                    }
                  />
                </div>
                <label
                  className="estimate-correction-billing-check"
                  title="Если отмечено, введённая сумма включает НДС"
                >
                  <input
                    type="checkbox"
                    checked={clientAmountWithNds}
                    onChange={handleClientAmountWithNdsChange}
                    disabled={
                      saving ||
                      correctionsLoading ||
                      incomeCorrectionSaving ||
                      !canEditCurrentCorrection
                    }
                  />
                  НДС включен в сумму
                </label>
              </div>
              <div className="estimate-correction-compare">
                <div className="estimate-correction-compare-item">
                  <div className="estimate-correction-compare-label">
                    Выставлено заказчику
                  </div>
                  <div className="estimate-correction-compare-value">
                    {formatCompareMoney(correctionClientContract)}
                  </div>
                  <div className="estimate-correction-compare-hint">
                    {billedRecordsCount
                      ? `В доход без НДС ${formatCompareMoney(
                          correctionClientIncome,
                        )}`
                      : "Ещё не указано"}
                  </div>
                </div>
                <div className="estimate-correction-compare-item">
                  <div className="estimate-correction-compare-label">
                    Корректировки по смете
                  </div>
                  <div className="estimate-correction-compare-value">
                    {formatCompareMoney(correctionExtraMoney)}
                  </div>
                  <div className="estimate-correction-compare-hint">
                    Себестоимость трудозатрат
                  </div>
                </div>
                <div className="estimate-correction-compare-item">
                  <div className="estimate-correction-compare-label">
                    Разница
                  </div>
                  <div
                    className={`estimate-correction-compare-value${
                      netProfitDelta > 0
                        ? " is-positive"
                        : netProfitDelta < 0
                          ? " is-negative"
                          : ""
                    }`}
                  >
                    {netProfitDelta === 0
                      ? formatCompareMoney(0)
                      : `${formatSignedDelta(netProfitDelta, { money: true })} ₸`}
                  </div>
                  <div className="estimate-correction-compare-hint">
                    Доход без НДС минус смета
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="estimate-history-btn"
              onClick={() => setShowCorrectionDrawer(true)}
              disabled={saving}
            >
              {areCorrectionsFrozen ? "Список корректировок" : "Откатить"}
            </button>
          </div>
        )}
        {!isReadOnly && !effectiveIsEditable && (
          <div className="estimate-locked-banner">
            <span className="estimate-locked-banner-icon">⚠️</span>
            <span className="estimate-locked-banner-text">
              Согласованную или отправленную на согласование смету нельзя
              редактировать. Чтобы внести изменения в базу, нажмите «Создать
              новую версию».
            </span>
          </div>
        )}
        <div className="estimate-dashboard">
          {/* 1. План рентабельности проекта — первый, на всю ширину */}
          <div className="estimate-profit-block estimate-profit-block-full">
            <div className="estimate-profit-title">
              План рентабельности проекта
            </div>
            <div
              style={{
                marginTop: "6px",
                marginBottom: "12px",
                fontSize: "12px",
                color: "#6c757d",
              }}
            >
              Примечание: итоговые значения в шапке обновляются только после
              сохранения. Показанные после калькуляции данные — предварительные.
            </div>
            <div className="estimate-profit-grid">
              <div
                className={`estimate-profit-card${
                  hasCorrectionIncomeChange ? " correction-sum-changed" : ""
                }`}
              >
                <div className="estimate-profit-icon">
                  <img src={Coinssvg} alt="Доход" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">Доход</div>
                  <div className="estimate-profit-hint">
                    {ndsPercent > 0
                      ? `Сумма основного договора − НДС ${ndsPercentLabel}%`
                      : "Сумма основного договора без НДС"}
                  </div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.income.toLocaleString()}
                    {hasCorrectionIncomeChange ? (
                      <span className="correction-sum-delta">
                        {correctionIncomeDeltaLabel}
                      </span>
                    ) : null}
                  </div>
                  <div className="estimate-profit-percent">
                    {profitData.incomePercent.toFixed(2)}%
                  </div>
                </div>
              </div>

              <div
                className={`estimate-profit-card${
                  hasCorrectionMoneyChange ? " correction-sum-changed" : ""
                }`}
              >
                <div className="estimate-profit-icon">
                  <img src={Calc} alt="Себестоимость" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">
                    Себестоимость - План
                  </div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.cost.toLocaleString()}
                    {hasCorrectionMoneyChange ? (
                      <span className="correction-sum-delta">
                        {correctionMoneyDeltaLabel}
                      </span>
                    ) : null}
                  </div>
                  <div className="estimate-profit-percent">
                    {headerData.cost_price_percent !== undefined &&
                    headerData.cost_price_percent !== null
                      ? Number(headerData.cost_price_percent).toFixed(2)
                      : profitData.costPercent.toFixed(2)}
                    %
                  </div>
                </div>
              </div>

              <div
                className={`estimate-profit-card${
                  hasCorrectionIndirectChange ? " correction-sum-changed" : ""
                }`}
              >
                <div className="estimate-profit-icon">
                  <img src={House} alt="Косвенные расходы" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">Косвенные расходы</div>
                  <div className="estimate-profit-hint">
                    {expensePercentLabel}% от дохода без НДС
                  </div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.expense.toLocaleString()}
                    {hasCorrectionIndirectChange ? (
                      <span className="correction-sum-delta">
                        {correctionIndirectDeltaLabel}
                      </span>
                    ) : null}
                  </div>
                  <div className="estimate-profit-percent">
                    {profitData.expensePercent.toFixed(2)}%
                  </div>
                </div>
              </div>

              <div
                className={`estimate-profit-card${
                  hasNetProfitCorrectionChange ? " correction-sum-changed" : ""
                }`}
              >
                <div className="estimate-profit-icon">
                  <img src={Sigma} alt="Чистая прибыль" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">Чистая прибыль</div>
                  <div className="estimate-profit-hint">
                    Доход − себестоимость − косвенные
                  </div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.netProfit.toLocaleString()}
                    {hasNetProfitCorrectionChange ? (
                      <span
                        className={`correction-sum-delta${
                          netProfitDelta < 0 ? " is-negative" : ""
                        }`}
                      >
                        {formatSignedDelta(netProfitDelta, { money: true })}
                      </span>
                    ) : null}
                  </div>
                  <div className="estimate-profit-percent">
                    {profitData.netProfitPercent.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Сумма основного договора и Сумма за вычетом НДС — в один ряд */}
          <div className="estimate-sums-row">
            <div
              className={`estimate-sum-card main${
                hasCorrectionContractChange ? " correction-sum-changed" : ""
              }`}
            >
              <div className="estimate-sum-label">{sums[0].label}</div>
              <div className="estimate-sum-value">
                <span className="currency">₸</span>{" "}
                {sums[0].value.toLocaleString()}
                {hasCorrectionContractChange ? (
                  <span className="correction-sum-delta">
                    {correctionContractDeltaLabel}
                  </span>
                ) : null}
              </div>
            </div>
            {headerData.nds_percent > 0 && (
              <div
                className={`estimate-sum-card gray${
                  hasCorrectionIncomeChange ? " correction-sum-changed" : ""
                }`}
              >
                <div className="estimate-sum-label">
                  Сумма за вычетом НДС {headerData.nds_percent}%
                </div>
                <div className="estimate-sum-value">
                  <span className="currency">₸</span>{" "}
                  {sums[1].value.toLocaleString()}
                  {hasCorrectionIncomeChange ? (
                    <span className="correction-sum-delta">
                      {correctionIncomeDeltaLabel}
                    </span>
                  ) : null}
                </div>
                <div className="estimate-sum-nds">
                  Сумма НДС <span className="currency">₸</span>{" "}
                  {sums[1].ndsValue.toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {/* 3. На разработку, Себестоимость, Отклонения */}
          <div className="estimate-list-block">
            <div className="estimate-list-items">
              <div className="estimate-list-item blue">
                <div className="estimate-list-dot blue"></div>
                <div className="estimate-list-content">
                  <div className="estimate-list-label">На разработку</div>
                  <div className="estimate-list-value">
                    {canEditDevelopmentPercent ? (
                      <span className="estimate-development-percent-control">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="estimate-development-percent-input"
                          value={developmentPercentDraft}
                          onChange={handleDevelopmentPercentChange}
                          onFocus={handleDevelopmentPercentFocus}
                          disabled={isDevelopmentPercentSubmitting || saving}
                          aria-label="Процент на разработку"
                        />
                        <span className="estimate-development-percent-unit">
                          %
                        </span>
                        {isDevelopmentPercentEditing && (
                          <span
                            className="estimate-development-percent-actions"
                            role="group"
                          >
                            <button
                              type="button"
                              className="estimate-development-percent-action estimate-development-percent-action-apply"
                              onClick={handleDevelopmentPercentApply}
                              disabled={
                                isDevelopmentPercentSubmitting || saving
                              }
                              title="Применить"
                              aria-label="Применить"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className="estimate-development-percent-action estimate-development-percent-action-cancel"
                              onClick={handleDevelopmentPercentCancel}
                              disabled={
                                isDevelopmentPercentSubmitting || saving
                              }
                              title="Отменить"
                              aria-label="Отменить"
                            >
                              ✕
                            </button>
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="estimate-development-percent-readonly">
                        {formatPercentDisplay(
                          Number(
                            headerData.contract_value_construction_percent,
                          ),
                        )}
                        %
                      </span>
                    )}
                    <span className="currency">₸</span>{" "}
                    {sums[2].value.toLocaleString()}
                  </div>
                </div>
              </div>
              <div
                className={`estimate-list-item green${
                  hasCorrectionMoneyChange ? " correction-sum-changed" : ""
                }`}
              >
                <div className="estimate-list-dot green"></div>
                <div className="estimate-list-content">
                  <div className="estimate-list-label">Себестоимость</div>
                  <div className="estimate-list-value">
                    <span className="currency">₸</span>{" "}
                    {sums[3].value.toLocaleString()}
                    {hasCorrectionMoneyChange ? (
                      <span className="correction-sum-delta">
                        {correctionMoneyDeltaLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div
                className={`estimate-list-item estimate-list-item--deviation ${deviationColor}${
                  hasNetProfitCorrectionChange ? " correction-sum-changed" : ""
                }`.trim()}
              >
                <div className={`estimate-list-dot ${deviationColor}`}></div>
                <div className="estimate-list-content">
                  <div className="estimate-list-label">Отклонения</div>
                  <div className="estimate-list-value">
                    <span className="currency">₸</span>{" "}
                    {deviation.toLocaleString()}
                    {hasNetProfitCorrectionChange ? (
                      <span
                        className={`correction-sum-delta${
                          netProfitDelta < 0 ? " is-negative" : ""
                        }`}
                      >
                        {formatSignedDelta(netProfitDelta, { money: true })}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              {showAupAhoBonusRow && (
                <div className="estimate-list-item orange">
                  <div className="estimate-list-dot orange"></div>
                  <div className="estimate-list-content">
                    <div className="estimate-list-label">
                      Бонусы АУП и АХО
                      {aupAhoBonusPercentValue > 0
                        ? ` ${formatPercentDisplay(aupAhoBonusPercentValue)}%`
                        : ""}
                    </div>
                    <div className="estimate-list-value">
                      <span className="currency">₸</span>{" "}
                      {aupAhoBonusAmount.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="estimate-table-block">
          <div className="estimate-bottom-tabs">
            {bottomTabs.map((tab) => (
              <button
                key={tab.id}
                className={`estimate-bottom-tab-btn${
                  activeBottomTab === tab.id ? " active" : ""
                }`}
                onClick={() => {
                  if (!saving) {
                    setActiveBottomTab(tab.id);
                    // Сохраняем активную нижнюю вкладку в localStorage
                    localStorage.setItem(
                      `estimate_activeBottomTab_${projectId}`,
                      tab.id,
                    );
                  }
                }}
                disabled={saving}
                style={{
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* WorkTab всегда загружен для основной логики сохранения */}
          <div
            style={{ display: activeBottomTab === "work" ? "block" : "none" }}
          >
            <WorkTab
              ref={workTabRef}
              projectId={projectId}
              isSaving={saving}
              isEditable={effectiveIsEditable}
            />
          </div>

          {/* Quality, Project и BIM всегда смонтированы, чтобы трудозатраты и разделы не сбрасывались при уходе со вкладки */}
          <div
            style={{
              display: activeBottomTab === "project" ? "block" : "none",
            }}
          >
            <ProjectTab ref={projectTabRef} isEditable={effectiveIsEditable} />
          </div>
          {activeBottomTab === "lead" && (
            <LeadTab ref={leadTabRef} isEditable={effectiveIsEditable} />
          )}
          <div
            style={{
              display: activeBottomTab === "quality" ? "block" : "none",
            }}
          >
            <QualityTab ref={qualityTabRef} isEditable={effectiveIsEditable} />
          </div>
          <div
            style={{ display: activeBottomTab === "bim" ? "block" : "none" }}
          >
            <BimTab ref={bimTabRef} isEditable={effectiveIsEditable} />
          </div>
          <div
            style={{
              display: activeBottomTab === "contract" ? "block" : "none",
            }}
          >
            <ContractSectionsTab
              ref={contractSectionsTabRef}
              projectId={projectId}
              isSaving={saving}
              isEditable={effectiveIsEditable}
            />
          </div>
          <div className="estimate-actions">
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                marginBottom: "8px",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              💡 Кнопка "Сохранить" сохраняет данные со всех вкладок
              одновременно
            </div>
            <button
              className="btn-send-approval"
              onClick={() => setShowApprovalModal(true)}
              disabled={saving || isSubmittingApproval || !effectiveIsEditable}
              style={{
                opacity:
                  saving || isSubmittingApproval || !effectiveIsEditable
                    ? 0.6
                    : 1,
                cursor:
                  saving || isSubmittingApproval || !effectiveIsEditable
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isSubmittingApproval
                ? "Отправка..."
                : "Отправить на согласование"}
            </button>
            {canSendCorrectionApproval && (
              <button
                className="btn-send-approval btn-send-correction-approval"
                onClick={() => setShowCorrectionApprovalModal(true)}
                disabled={
                  saving || isSubmittingCorrectionApproval || correctionsLoading
                }
              >
                {isSubmittingCorrectionApproval
                  ? "Отправка..."
                  : "Отправить корректировку на согласование"}
              </button>
            )}
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={
                  saving ||
                  isSubmittingApproval ||
                  !effectiveIsEditable ||
                  (activeBottomTab !== "contract" &&
                    ((headerData?.percentTotalFromTable ??
                      headerData?.total_construction_design
                        ?.expenditures_percent?.threshold) > 101 ||
                      (workTabRef.current &&
                        workTabRef.current.hasValidationErrors &&
                        workTabRef.current.hasValidationErrors())))
                }
                style={{
                  opacity:
                    saving ||
                    !effectiveIsEditable ||
                    (activeBottomTab !== "contract" &&
                      ((headerData?.percentTotalFromTable ??
                        headerData?.total_construction_design
                          ?.expenditures_percent?.threshold) > 101 ||
                        (workTabRef.current &&
                          workTabRef.current.hasValidationErrors &&
                          workTabRef.current.hasValidationErrors())))
                      ? 0.6
                      : 1,
                  cursor:
                    saving ||
                    !effectiveIsEditable ||
                    (activeBottomTab !== "contract" &&
                      ((headerData?.percentTotalFromTable ??
                        headerData?.total_construction_design
                          ?.expenditures_percent?.threshold) > 101 ||
                        (workTabRef.current &&
                          workTabRef.current.hasValidationErrors &&
                          workTabRef.current.hasValidationErrors())))
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {saving ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid #ffffff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Сохранение...
                  </div>
                ) : (
                  "Сохранить"
                )}
              </button>
              {activeBottomTab !== "contract" &&
                ((headerData?.percentTotalFromTable ??
                  headerData?.total_construction_design?.expenditures_percent
                    ?.threshold) > 101 ||
                  (workTabRef.current &&
                    workTabRef.current.hasValidationErrors &&
                    workTabRef.current.hasValidationErrors())) && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      right: 0,
                      transform: "translateY(-8px)",
                      background: "#fff8f1",
                      color: "#b26a00",
                      border: "1px solid #ffe0b2",
                      borderRadius: "6px",
                      padding: "8px 10px",
                      fontSize: "12px",
                      whiteSpace: "normal",
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                      maxWidth: "min(360px, calc(100vw - 32px))",
                      zIndex: 10,
                      pointerEvents: "none",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    }}
                  >
                    {(headerData?.percentTotalFromTable ??
                      headerData?.total_construction_design
                        ?.expenditures_percent?.threshold) > 101
                      ? "Сумма долей превышает 101%. Сохранение недоступно."
                      : "% доля и средняя ставка по всем разделам должны быть > 0."}
                  </div>
                )}
            </div>
            {/* <button
              className="btn-print-pdf"
              disabled={saving}
              style={{
                opacity: saving ? 0.6 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M14 2H2V14H14V2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6H10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 9H10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 12H8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Печать PDF
            </button> */}
          </div>
        </div>
        <VersionHistoryDrawer
          open={showVersionDrawer}
          onClose={() => setShowVersionDrawer(false)}
          projectId={projectId}
          onVersionRestored={() => loadEstimateData(projectId)}
        />
        <CorrectionHistoryDrawer
          open={showCorrectionDrawer}
          onClose={() => setShowCorrectionDrawer(false)}
          projectId={projectId}
          sectionIds={correctionSectionIds}
          sectionsLookup={correctionSectionsLookup}
          corrections={corrections}
          incomeCorrections={incomeCorrections}
          incomeCorrectionTotal={incomeCorrectionTotal}
          onRestoreCorrection={
            areCorrectionsFrozen ? undefined : handleRestoreCorrection
          }
          onDeleteCorrection={
            isReadOnly || isCorrectionsPending
              ? undefined
              : handleDeleteCorrection
          }
          onRestoreBase={
            areCorrectionsFrozen ? undefined : handleRestoreBaseEstimate
          }
          restoring={correctionsLoading}
          readOnly={areCorrectionsFrozen}
        />
        <ApprovalModal
          open={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          onSubmit={handleApprovalSubmit}
          isSubmitting={isSubmittingApproval}
        />
        <ApprovalModal
          open={showCorrectionApprovalModal}
          onClose={() => setShowCorrectionApprovalModal(false)}
          onSubmit={handleCorrectionApprovalSubmit}
          isSubmitting={isSubmittingCorrectionApproval}
          title="Согласование корректировок"
          warning="Подтвердите отправку корректировок на согласование. После этого их нельзя будет редактировать, пока согласование не завершится или не будет отозвано."
        />
        <SuccessModal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message={successMessage}
        />
        <ErrorModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message={errorMessage}
        />
      </div>
    </EstimateContext.Provider>
  );
};

const Estimate = ({ projectId, onSave, isReadOnly = false }) => {
  return (
    <EstimateStateProvider>
      <EstimateComponent
        projectId={projectId}
        onSave={onSave}
        isReadOnly={isReadOnly}
      />
    </EstimateStateProvider>
  );
};

export default Estimate;
